import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, MagnifyingGlass, Check, Warning, Plus, Trash, PencilSimple, Paperclip, Microphone, Stop, Play, Pause, File as FileIcon } from '@phosphor-icons/react';
import { Md } from '@/components/Md';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PatientSearch from '@/components/PatientSearch';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = { aberto: 'bg-brand-gold/20 text-brand-gold-hover', em_resposta: 'bg-brand-mint/20 text-brand-green', resolvido: 'bg-brand-bg text-brand-muted' };

export default function MensagensAdminPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [tab, setTab] = useState('chat');
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [requests, setRequests] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [response, setResponse] = useState('');
  const [reqFilter, setReqFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [limits, setLimits] = useState([]);
  const [catForm, setCatForm] = useState({ name: '', description: '', color: '#657161' });
  const [showCatForm, setShowCatForm] = useState(false);
  const [scheduledMsgs, setScheduledMsgs] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvPatient, setNewConvPatient] = useState('');
  const [schedForm, setSchedForm] = useState({ patient_id: '', content: '', trigger_type: 'date', scheduled_at: '' });
  const [clients, setClients] = useState([]);
  const messagesEndRef = useRef(null);

  const fetchConvs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/conversations`); setConversations(data); } catch {}
  }, []);
  const fetchRequests = useCallback(async () => {
    try { const params = reqFilter ? `?status=${reqFilter}` : ''; const { data } = await axios.get(`${API}/api/structured-requests${params}`); setRequests(data); } catch {}
  }, [reqFilter]);
  const fetchConfig = useCallback(async () => {
    // Resiliente: um endpoint que falhe (ex.: /admin/users é admin-only e dá 403 à
    // nutricionista) não pode descartar a config que carregou com sucesso.
    const [cRes, lRes, sRes, clRes] = await Promise.allSettled([
      axios.get(`${API}/api/admin/request-categories`),
      axios.get(`${API}/api/admin/communication-limits`),
      axios.get(`${API}/api/scheduled-messages`),
      axios.get(`${API}/api/patients?limit=500`),
    ]);
    if (cRes.status === 'fulfilled') setCategories(cRes.value.data || []);
    if (lRes.status === 'fulfilled') setLimits(lRes.value.data || []);
    if (sRes.status === 'fulfilled') setScheduledMsgs(sRes.value.data || []);
    if (clRes.status === 'fulfilled') setClients(clRes.value.data || []);
  }, []);
  useEffect(() => { fetchConvs(); fetchRequests(); fetchConfig(); }, [fetchConvs, fetchRequests, fetchConfig]);
  useEffect(() => { if (tab === 'chat') { const i = setInterval(fetchConvs, 30000); return () => clearInterval(i); } }, [tab, fetchConvs]);

  const openConv = async (conv) => {
    setSelectedConv(conv);
    try {
      const { data } = await axios.get(`${API}/api/conversations/${conv.id}/messages`);
      setMessages(data);
      if (conv.unread_by_nutri > 0) await axios.put(`${API}/api/conversations/${conv.id}/mark-read`);
      fetchConvs();
    } catch {}
  };
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (selectedConv) { const i = setInterval(() => openConv(selectedConv), 15000); return () => clearInterval(i); } }, [selectedConv]);

  const sendMsg = async () => {
    if (!newMsg.trim() || !selectedConv) return;
    try { await axios.post(`${API}/api/conversations/${selectedConv.id}/messages`, { content: newMsg }); setNewMsg(''); openConv(selectedConv); } catch { toast.error('Erro'); }
  };
  const startConv = async () => {
    if (!newConvPatient) return;
    try {
      const { data } = await axios.post(`${API}/api/conversations/start`, { patient_id: newConvPatient });
      setShowNewConv(false); setNewConvPatient('');
      await fetchConvs();
      openConv(data);
    } catch { toast.error('Não foi possível iniciar a conversa'); }
  };
  const respondReq = async () => {
    if (!response.trim() || !selectedReq) return;
    try { await axios.put(`${API}/api/structured-requests/${selectedReq.id}/respond`, { response }); toast.success('Resposta enviada'); setResponse(''); setSelectedReq(null); fetchRequests(); } catch { toast.error('Erro'); }
  };
  const resolveReq = async (id) => {
    try { await axios.put(`${API}/api/structured-requests/${id}/status`, { status: 'resolvido' }); fetchRequests(); } catch {}
  };

  const filteredConvs = conversations.filter(c => !filter || (c.patient_name || '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="fade-in" data-testid="mensagens-admin-page">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Mensagens</h2>
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('chat')} className={`flex-1 py-2 rounded-xl text-sm font-medium ${tab === 'chat' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Conversas</button>
        <button onClick={() => setTab('pedidos')} className={`flex-1 py-2 rounded-xl text-sm font-medium ${tab === 'pedidos' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Pedidos</button>
        <button onClick={() => setTab('config')} className={`px-3 py-2 rounded-xl text-sm font-medium ${tab === 'config' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Configurar</button>
        <button onClick={() => setTab('programadas')} className={`px-3 py-2 rounded-xl text-sm font-medium ${tab === 'programadas' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Programadas</button>
      </div>

      {tab === 'chat' && (
        <div className="flex gap-4" style={{ height: 'calc(100vh - 250px)' }}>
          {/* Conversation list */}
          <div className="w-72 flex-shrink-0 overflow-y-auto space-y-1">
            <button onClick={() => { setNewConvPatient(''); setShowNewConv(true); }} data-testid="nova-mensagem-btn" className="w-full bg-brand-green text-white rounded-xl py-2 text-xs font-medium flex items-center justify-center gap-1 mb-2"><Plus size={13} /> Nova mensagem</button>
            <div className="relative mb-2"><MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" /><Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Pesquisar..." className="pl-8 rounded-xl border-brand-border text-xs h-8" /></div>
            {filteredConvs.map(c => (
              <button key={c.id} onClick={() => openConv(c)} className={`w-full text-left p-3 rounded-xl transition-all ${selectedConv?.id === c.id ? 'bg-brand-green/5 border border-brand-green/20' : 'hover:bg-brand-bg'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-brand-text flex-1 truncate">{c.patient_name}</span>
                  {c.unread_by_nutri > 0 && <span className="w-5 h-5 rounded-full bg-brand-green text-white text-[9px] flex items-center justify-center">{c.unread_by_nutri}</span>}
                </div>
                <p className="text-[10px] text-brand-muted truncate mt-0.5">{c.last_message || 'Sem mensagens'}</p>
              </button>
            ))}
            {filteredConvs.length === 0 && <p className="text-xs text-brand-muted text-center py-4">Sem conversas.</p>}
          </div>
          {/* Chat area */}
          <div className="flex-1 flex flex-col bg-white border border-brand-border rounded-2xl overflow-hidden">
            {selectedConv ? (<>
              <div className="p-3 border-b border-brand-border"><p className="text-sm font-medium text-brand-text">{selectedConv.patient_name}</p><p className="text-[9px] text-brand-muted">{selectedConv.patient_type}</p></div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'nutri' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-3 py-2 ${m.sender_type === 'nutri' ? 'bg-brand-green/10 text-brand-text' : 'bg-brand-mint/30 text-brand-text'}`}>
                      {m.content && <Md as="p" className="text-sm">{m.content}</Md>}
                      {(m.attachments || []).map((att, ai) => (
                        <div key={ai} className="mt-1">
                          {att.type === 'image' && <img src={`${API}/api/files/${att.url}`} alt="" className="max-w-[200px] rounded-xl cursor-pointer" onClick={() => window.open(`${API}/api/files/${att.url}`, '_blank')} />}
                          {att.type === 'audio' && <button className="text-xs text-brand-green flex items-center gap-1" onClick={() => { const a = new Audio(`${API}/api/files/${att.url}`); a.play(); }}><Play size={12} weight="fill" /> Mensagem de voz</button>}
                          {att.type === 'file' && <a href={`${API}/api/files/${att.url}`} target="_blank" rel="noreferrer" className="text-xs text-brand-green flex items-center gap-1"><FileIcon size={12} /> {att.filename}</a>}
                        </div>
                      ))}
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[8px] text-brand-muted">{m.created_at?.slice(11, 16)}</span>
                        {m.is_automated && <span className="text-[7px] bg-purple-50 text-purple-500 px-1 py-0.5 rounded">auto</span>}
                        {m.sender_type === 'nutri' && m.read_at && <span className="text-[7px] text-brand-green flex items-center gap-0.5"><Check size={8} /> lida {m.read_at.slice(11,16)}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-3 border-t border-brand-border flex gap-2 items-end">
                <label className="p-2 text-brand-muted hover:text-brand-green cursor-pointer"><Paperclip size={16} /><input type="file" accept="image/*,.pdf,.doc,.docx" onChange={async e => { const f = e.target.files?.[0]; if (!f || !selectedConv) return; try { const fd = new FormData(); fd.append('file', f); const { data: att } = await axios.post(`${API}/api/chat-upload`, fd); await axios.post(`${API}/api/conversations/${selectedConv.id}/messages`, { content: '', attachments: [att] }); openConv(selectedConv); } catch { toast.error('Erro'); } }} className="hidden" /></label>
                <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }} placeholder="Responder..." className="flex-1 rounded-xl border-brand-border" />
                <button onClick={sendMsg} disabled={!newMsg.trim()} className="bg-brand-green text-white rounded-xl px-4 py-2 disabled:opacity-40"><PaperPlaneTilt size={18} /></button>
              </div>
            </>) : <div className="flex-1 flex items-center justify-center text-brand-muted text-sm">Seleciona uma conversa</div>}
          </div>
        </div>
      )}

      {/* Nova conversa dialog */}
      <Dialog open={showNewConv} onOpenChange={setShowNewConv}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="nova-mensagem-dialog"><DialogHeader><DialogTitle className="font-heading text-brand-text">Nova mensagem</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Paciente</Label>
              <PatientSearch value={newConvPatient} onChange={v => setNewConvPatient(v)} />
            </div>
            <button onClick={startConv} disabled={!newConvPatient} data-testid="nova-mensagem-iniciar-btn" className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40">Iniciar conversa</button>
          </div>
        </DialogContent>
      </Dialog>

      {tab === 'pedidos' && (
        <div>
          <div className="flex gap-2 mb-3">
            {['', 'aberto', 'em_resposta', 'resolvido'].map(s => (
              <button key={s} onClick={() => setReqFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${reqFilter === s ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{s || 'Todos'}</button>
            ))}
          </div>
          {requests.length === 0 ? <p className="text-center text-brand-muted py-8">Sem pedidos.</p> : (
            <div className="space-y-2">
              {requests.map(r => (
                <div key={r.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between" data-testid={`req-${r.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                      {r.priority === 'urgente' && <span className="text-[8px] text-red-500 font-bold">URGENTE</span>}
                      <span className="text-[9px] text-brand-muted">{r.patient_name}</span>
                    </div>
                    <p className="text-sm font-medium text-brand-text">{r.subject}</p>
                    <p className="text-[10px] text-brand-muted">{r.created_at?.split('T')[0]}</p>
                  </div>
                  <div className="flex gap-1">
                    {r.status !== 'resolvido' && <button onClick={() => { setSelectedReq(r); setResponse(''); }} className="text-xs text-brand-green hover:underline">Responder</button>}
                    {r.status !== 'resolvido' && <button onClick={() => resolveReq(r.id)} className="text-xs text-brand-muted hover:underline ml-2">Resolver</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Respond dialog */}
          <Dialog open={!!selectedReq} onOpenChange={v => { if (!v) setSelectedReq(null); }}>
            <DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">Responder pedido</DialogTitle></DialogHeader>
              {selectedReq && (
                <div className="space-y-3">
                  <div className="bg-brand-bg/50 rounded-xl p-3"><Md as="p" className="text-xs font-medium text-brand-text mb-1">{selectedReq.subject}</Md><Md as="p" className="text-xs text-brand-muted">{selectedReq.description}</Md></div>
                  <Textarea value={response} onChange={e => setResponse(e.target.value)} placeholder="A tua resposta..." rows={4} className="rounded-xl border-brand-border" />
                  <button onClick={respondReq} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Enviar resposta</button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === 'config' && (
        <div className="space-y-5">
          {/* Categories */}
          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Categorias de pedidos</h3>
              <button onClick={() => { setCatForm({ name: '', description: '', color: '#657161' }); setShowCatForm(true); }} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Nova</button>
            </div>
            {categories.length === 0 ? <p className="text-xs text-brand-placeholder py-2">Sem categorias.</p> : (
              <div className="space-y-1.5">
                {categories.map(c => (
                  <div key={c.id} className="flex items-center gap-2 bg-brand-bg/30 rounded-xl px-3 py-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="text-xs text-brand-text flex-1">{c.name}</span>
                    <button onClick={async () => { const ok = await confirmDelete({ description: `Vai apagar a categoria "${c.name}".` }); if (!ok) return; await axios.delete(`${API}/api/admin/request-categories/${c.id}`); fetchConfig(); }} className="text-brand-placeholder hover:text-red-500"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Limits */}
          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Limites por programa</h3>
            {['gravida','pos_parto','introducao_alimentar','emagrecimento','pre_concecao'].map(prog => {
              const l = limits.find(x => x.program_type === prog) || {};
              return (
                <div key={prog} className="flex items-center gap-3 mb-2">
                  <span className="text-xs text-brand-text w-32">{prog.replace(/_/g,' ')}</span>
                  <Input type="number" placeholder="Msgs/mês" defaultValue={l.chat_messages_per_month || ''} className="w-20 rounded-lg border-brand-border text-xs h-7" onBlur={async e => { const v = e.target.value ? parseInt(e.target.value) : null; await axios.put(`${API}/api/admin/communication-limits/${prog}`, { chat_messages_per_month: v, structured_requests_per_month: l.structured_requests_per_month || null }); fetchConfig(); }} />
                  <Input type="number" placeholder="Pedidos/mês" defaultValue={l.structured_requests_per_month || ''} className="w-20 rounded-lg border-brand-border text-xs h-7" onBlur={async e => { const v = e.target.value ? parseInt(e.target.value) : null; await axios.put(`${API}/api/admin/communication-limits/${prog}`, { chat_messages_per_month: l.chat_messages_per_month || null, structured_requests_per_month: v }); fetchConfig(); }} />
                </div>
              );
            })}
            <p className="text-[9px] text-brand-placeholder mt-1">Vazio = ilimitado. Valores guardam automaticamente ao sair do campo.</p>
          </div>
          {/* Add category dialog */}
          <Dialog open={showCatForm} onOpenChange={setShowCatForm}>
            <DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">Nova categoria</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} placeholder="Nome da categoria" className="rounded-xl border-brand-border" />
                <Input value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} placeholder="Descrição (opcional)" className="rounded-xl border-brand-border" />
                <div className="flex items-center gap-2"><span className="text-xs text-brand-muted">Cor:</span><input type="color" value={catForm.color} onChange={e => setCatForm({ ...catForm, color: e.target.value })} className="w-8 h-8 rounded border-0" /></div>
                <button onClick={async () => { await axios.post(`${API}/api/admin/request-categories`, catForm); toast.success('Categoria criada'); setShowCatForm(false); fetchConfig(); }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Criar</button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === 'programadas' && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => { setSchedForm({ patient_id: '', content: '', trigger_type: 'date', scheduled_at: '' }); setShowScheduleForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1"><Plus size={12} /> Nova mensagem</button>
          </div>
          {scheduledMsgs.length === 0 ? <p className="text-center text-brand-muted py-8 text-sm">Sem mensagens programadas.</p> : (
            <div className="space-y-2">
              {scheduledMsgs.map(m => (
                <div key={m.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-brand-text">{m.content?.slice(0, 80)}{m.content?.length > 80 ? '...' : ''}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-brand-muted">Para: {clients.find(c => c.user_id === m.patient_id)?.name || m.patient_id?.slice(0,8)}</span>
                      <span className="text-[9px] text-brand-muted">Data: {m.scheduled_at}</span>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${m.status === 'enviada' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{m.status}</span>
                      {m.is_automated || m.trigger_type ? <span className="text-[7px] bg-purple-50 text-purple-500 px-1 py-0.5 rounded">auto</span> : null}
                    </div>
                  </div>
                  {m.status === 'pendente' && (
                    <div className="flex gap-1">
                      <button onClick={() => { setSchedForm({ patient_id: m.patient_id, content: m.content, trigger_type: m.trigger_type || 'date', scheduled_at: m.scheduled_at }); setShowScheduleForm(true); }} className="text-brand-placeholder hover:text-brand-green p-1"><PencilSimple size={13} /></button>
                      <button onClick={async () => { const ok = await confirmDelete({ description: `Vai apagar a mensagem programada para ${m.scheduled_at}.` }); if (!ok) return; await axios.delete(`${API}/api/scheduled-messages/${m.id}`); fetchConfig(); }} className="text-brand-placeholder hover:text-red-500 p-1"><Trash size={13} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <Dialog open={showScheduleForm} onOpenChange={setShowScheduleForm}>
            <DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">Mensagem programada</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-brand-muted mb-1 block">Paciente</Label>
                  <PatientSearch value={schedForm.patient_id} onChange={v => setSchedForm({ ...schedForm, patient_id: v })} />
                </div>
                <div><Label className="text-xs text-brand-muted mb-1 block">Mensagem</Label><Textarea value={schedForm.content} onChange={e => setSchedForm({ ...schedForm, content: e.target.value })} rows={3} className="rounded-xl border-brand-border resize-none" /></div>
                <div><Label className="text-xs text-brand-muted mb-1 block">Tipo de gatilho</Label>
                  <select value={schedForm.trigger_type} onChange={e => setSchedForm({ ...schedForm, trigger_type: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white">
                    <option value="date">Data específica</option>
                    <option value="days_after_start">N dias após início do programa</option>
                    <option value="days_before_consult">N dias antes da próxima consulta</option>
                    <option value="challenge_day">Dia X do desafio</option>
                  </select>
                </div>
                <div><Label className="text-xs text-brand-muted mb-1 block">{schedForm.trigger_type === 'date' ? 'Data de envio' : 'Valor (dias/número)'}</Label>
                  <Input type={schedForm.trigger_type === 'date' ? 'date' : 'text'} value={schedForm.scheduled_at} onChange={e => setSchedForm({ ...schedForm, scheduled_at: e.target.value })} className="rounded-xl border-brand-border" />
                </div>
                <button onClick={async () => { try { await axios.post(`${API}/api/scheduled-messages`, schedForm); toast.success('Mensagem programada'); setShowScheduleForm(false); fetchConfig(); } catch { toast.error('Erro'); } }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Programar</button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
